import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { VerticalTabComponent, TabItem } from '../vertical-tab/vertical-tab.component';
import { HorizontalTabComponent } from '../horizontal-tab/horizontal-tab.component';
import { BasicInfoComponent } from './basic-info/basic-info.component';
import { PlatformComponent } from './platform/platform.component';
import { CampaignPlatformApiItem, UtmTaggingComponent } from './utm-tagging/utm-tagging.component';
import { UserSegmentComponent } from './user-segment/user-segment.component';
import { OfferOverviewComponent } from './offer-overview/offer-overview.component';
import { OfferDetailsComponent } from './offer-details/offer-details.component';
import { UsageLimitsComponent } from './usage-limits/usage-limits.component';
import { EmailComponent } from './email/email.component';
import { TextMessageComponent } from './text-message/text-message.component';
import { FacebookComponent } from './facebook/facebook.component';
import { GoogleAdsComponent } from './google-ads/google-ads.component';
import { GoalsComponent } from './goals/goals.component';
import { ScheduleComponent } from './schedule/schedule.component';
import {
  OptionalSettingComponent,
  OptionalSettingSection,
} from './optional-setting/optional-setting.component';
import {
  isOptionalSettingsCompleteFromContents,
  isOptionalSettingsDeliveryComplete,
  isOptionalSettingsLandingComplete,
  isOptionalSettingsSuccessComplete,
} from './optional-setting/common-website-content.mapper';
import {
  DeliveryContentApi,
  findSmsContent,
  findEmailContent,
  findFacebookContent,
  findGoogleAdsContent,
} from './optional-setting/sms-delivery.mapper';
import { CampaignPreviewModalComponent } from './campaign-preview-modal/campaign-preview-modal.component';
import { CampaignSuccessModalComponent } from './campaign-success-modal/campaign-success-modal.component';
import { CampaignService } from '../../services/campaign.service';
import { ApiService } from '../../services/api.service';
import { Segment } from '../../shared/types/segment.interface';
import { LocationService } from '../../services/location.service';
import { LocationApiResponse } from '../../shared/types';
import { Observable, Subject, Subscription, debounceTime, finalize, takeUntil, tap } from 'rxjs';
import { CampaignDataManagerService } from './services/campaign-data-manager.service';
import { extractErrorMessage, sanitizeErrorMessage } from '../../shared/utils/error.util';
import { LoggerService } from '../../services/logger.service';

const PLATFORM_SECTIONS: { platform: string; section: string }[] = [
  { platform: 'email', section: 'Email' },
  { platform: 'sms', section: 'Text Message' },
  { platform: 'google-ads', section: 'Google Ads' },
];

type CampaignContentPlatform =
  | 'email'
  | 'delivery_email'
  | 'sms_delivery'
  | 'facebook'
  | 'sms'
  | 'mms'
  | 'mms_campaign'
  | 'rcs'
  | 'google'
  | 'common_website';

/**
 * Returns a default campaign data object with initial empty or null values.
 * @example
 * getDefaultCampaignData()
 * { name: '', description: '', campaign_status: 'draft', platform: [], segment: [], locations: [], packages: [], contents: [], goals: [], offer_name: '', promo_code: '', offer_description: '', offer_type: '', max_discount: null, offer_discount: null, offer_wash_type: '', quantity_limit: null, multi_use_limit: null, usage_type: '', schedule_start_date: '', schedule_start_time: '', schedule_end_date: '', schedule_end_time: '', schedule_recurrence: '' }
 * @returns {{any}} A default campaign data object populated with initial values.
 */
function getDefaultCampaignData(): any {
  return {
    name: '',
    description: '',
    campaign_status: 'draft',
    platform: [],
    segment: [],
    locations: [],
    packages: [],
    contents: [],
    goals: [],
    offer_name: '',
    promo_code: '',
    offer_description: '',
    offer_type: '',
    max_discount: null,
    offer_discount: null,
    offer_wash_type: '',
    quantity_limit: null,
    multi_use_limit: null,
    usage_type: '',
    schedule_start_date: '',
    schedule_start_time: '',
    schedule_end_date: '',
    schedule_end_time: '',
    schedule_recurrence: '',
  };
}

@Component({
  selector: 'app-campaign-editor',
  standalone: true,
  imports: [
    CommonModule,
    MatTooltipModule,
    VerticalTabComponent,
    HorizontalTabComponent,
    BasicInfoComponent,
    PlatformComponent,
    UtmTaggingComponent,
    UserSegmentComponent,
    OfferOverviewComponent,
    OfferDetailsComponent,
    UsageLimitsComponent,
    EmailComponent,
    TextMessageComponent,
    FacebookComponent,
    GoogleAdsComponent,
    OptionalSettingComponent,
    GoalsComponent,
    ScheduleComponent,
    CampaignPreviewModalComponent,
    CampaignSuccessModalComponent,
  ],
  templateUrl: './campaign-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './campaign-editor.component.scss',
})
export class CampaignEditorComponent implements OnInit, OnDestroy {
  private readonly logger = inject(LoggerService);
  @ViewChild('basicInfoComponent') basicInfoComponent!: BasicInfoComponent;

  activeTab = 'overview';
  showPreviewModal = false;
  showSuccessModal = false;
  campaignId: string | null = null;
  isMobileView = false;
  isPublishing = false;
  isOfferOverviewSaving = false;
  isOfferDetailsSaving = false;
  hasPromoCodeConflict = false;
  promoCodeConflictMessage = '';
  isNewCampaign = false; // Track if we started as a new campaign
  private destroy$ = new Subject<void>();
  private formDataSubject = new Subject<any>();
  /** In-flight campaign update (autosave or publish-prep); unsubscribe cancels the HTTP request. */
  private campaignUpdateSub: Subscription | null = null;
  /** Bumped to ignore stale update callbacks after cancel/supersede. */
  private campaignUpdateGeneration = 0;
  private isExplicitSaveInProgress = false;
  /** Bumped by every section edit. Identifies which edits a given save carried. */
  private formChangeSeq = 0;
  /** Highest formChangeSeq the API has accepted. */
  private persistedFormChangeSeq = 0;
  private createRequestInFlight = false;
  private pendingCreateSuccessCallbacks: (() => void)[] = [];

  // Campaign data structure
  campaignData: any = getDefaultCampaignData();

  // Segments list for mapping IDs to names
  segmentsList: Segment[] = [];

  // Locations list for mapping IDs to names
  locationsList: LocationApiResponse[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private campaignService: CampaignService,
    private messageService: NotificationService,
    private campaignDataManager: CampaignDataManagerService,
    private apiService: ApiService,
    private locationService: LocationService
  ) {}

  /**
   * Initialize the campaign editor component: checks screen size, loads segments and locations, determines whether this is a new or existing campaign, subscribes to route and query parameter changes, sets up debounced form handling, and updates tab states.
   * @example
   * gOnInit()
   * void
   * @returns {{void}} No return value; initializes component state, triggers data loads, and registers subscriptions for routing and form changes.
   **/
  ngOnInit(): void {
    // Check initial screen size
    this.checkScreenSize();

    // Load segments and locations for preview modal
    this.loadSegments();
    this.loadLocations();

    // Check initial query params to determine if this is a new campaign or editing
    const initialParams = this.route.snapshot.queryParams;
    const initialCampaignId = initialParams['campaignId'] || null;
    const isNewParam = initialParams['new'] === 'true';
    this.isNewCampaign = isNewParam;
    // If there's no campaignId initially, it's a new campaign
    if (!initialCampaignId) {
      this.campaignId = null;
      this.resetCampaignData();
    } else {
      // If there's a campaignId from the start, we're editing an existing campaign
      this.campaignId = initialCampaignId;
      this.loadCampaignData();
    }

    // Subscribe to query parameter changes (for when campaign is auto-created)
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.campaignId = params['campaignId'] || null;

      // Only update isNewCampaign if 'new' parameter is explicitly present in URL
      // Don't change isNewCampaign based on campaignId appearing
      if ('new' in params) {
        this.isNewCampaign = params['new'] === 'true';
      }
      // If 'new' parameter is not present, preserve the current isNewCampaign state
      // This ensures that when navigating between tabs, the state is preserved

      // Update tab disabled state based on campaignId
      this.updateTabDisabledState();
    });

    // Set up debounced form data collection (3 seconds)
    this.formDataSubject.pipe(debounceTime(3000), takeUntil(this.destroy$)).subscribe(() => {
      this.handleFormDataChange();
    });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const tab = params['tab'];
      const section = params['section'];

      if (tab) {
        this.activeTab = tab;
        this.initializeTabState(tab, section);
      }
    });

    // Update tab completion status
    this.updateTabCompletionStatus();
  }

  ngOnDestroy(): void {
    this.cancelPendingCampaignUpdate();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cancelPendingCampaignUpdate(): void {
    if (this.campaignUpdateSub) {
      this.campaignUpdateSub.unsubscribe();
      this.campaignUpdateSub = null;
    }
    this.campaignUpdateGeneration += 1;
  }

  /**
   * True while campaignData holds section edits the API has not accepted yet. A refetch that lands
   * in this window predates those edits, so applying it would silently discard what the user just
   * typed (CWS-1107). Stays armed when a save fails, and when fresh edits arrive mid-flight.
   */
  private get hasUnsavedFormChanges(): boolean {
    return this.formChangeSeq > this.persistedFormChangeSeq;
  }

  /** Records that the API accepted everything edited up to `seq`. Edits made since stay pending. */
  private commitFormChanges(seq: number): void {
    if (seq > this.persistedFormChangeSeq) {
      this.persistedFormChangeSeq = seq;
    }
  }

  // Collapsible section states - only one can be expanded at a time
  basicInfoExpanded = true;
  platformExpanded = false;
  utmTaggingExpanded = false;
  userSegmentExpanded = false;
  offerOverviewExpanded = true; // Start with Offer Overview expanded in offers tab
  offerDetailsExpanded = false;
  usageLimitsExpanded = false;
  emailExpanded = false;
  textMessageExpanded = false;
  facebookExpanded = false;
  googleAdsExpanded = false;
  optionalSettingsActiveSection: OptionalSettingSection | '' = 'landing';
  goalsExpanded = false;
  scheduleExpanded = false;

  // Progress tracking for each section
  sectionCompletionStatus: Record<string, boolean> = {
    'Basic Info': false,
    Platform: false,
    'UTM tagging': false,
    'User Segment': false,
    'Offer Overview': false,
    'Offer Details': false,
    'Usage Limits': false,
    Email: false,
    'Text Message': false,
    Facebook: false,
    'Google Ads': false,
    'Landing Page': false,
    'Success Page': false,
    'SMS/Email Delivery': false,
    'Optional Settings': false,
    Goals: false,
    Schedule: false,
  };
  tabs: TabItem[] = [
    {
      id: 'overview',
      title: 'Overview',
      icon: ``,
      subItems: ['Basic Info', 'Platform', 'UTM tagging', 'User Segment'],
    },
    {
      id: 'offers',
      title: 'Offers',
      icon: ``,
      subItems: ['Offer Overview', 'Offer Details', 'Usage Limits'],
    },
    {
      id: 'content',
      title: 'Content',
      icon: ``,
      subItems: ['Email', 'Text Message', 'Facebook', 'Google Ads'],
    },
    {
      id: 'advanced',
      title: 'Advanced',
      icon: ``,
      subItems: ['Optional Settings'],
    },
    {
      id: 'finish',
      title: 'Finish',
      icon: ``,
      subItems: ['Goals', 'Schedule'],
    },
  ];

  // Method to check if a section is completed
  isSectionCompleted(sectionName: string): boolean {
    return this.sectionCompletionStatus[sectionName] || false;
  }

  // Method to get completion percentage for a tab
  getTabCompletionPercentage(tabId: string): number {
    if (tabId === 'advanced') {
      return this.getTabCompletionPercentageForSubItems(this.getAdvancedProgressSections());
    }
    return this.getTabCompletionPercentageForSubItems(this.getFilteredSubItemsForTab(tabId));
  }

  /** Hidden progress slices for Advanced tab circle (sidebar shows only Optional Settings). */
  private getAdvancedProgressSections(): string[] {
    const sections = ['Landing Page', 'Success Page', 'SMS/Email Delivery'];
    return sections.filter(subItem => {
      if (subItem === 'SMS/Email Delivery') {
        return this.isPlatformSelected('sms') || this.isPlatformSelected('email');
      }
      return true;
    });
  }

  private getFilteredSubItemsForTab(tabId: string): string[] {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) {
      return [];
    }

    if (tab.id === 'overview') {
      return tab.subItems.filter(subItem => {
        if (subItem === 'User Segment') {
          return this.showUserSegmentSection;
        }
        return true;
      });
    }

    if (tab.id === 'content') {
      return tab.subItems.filter(subItem => {
        switch (subItem) {
          case 'Email':
            return this.isPlatformSelected('email');
          case 'Text Message':
            return this.isPlatformSelected('sms');
          case 'Facebook':
            return this.isPlatformSelected('facebook');
          case 'Google Ads':
            return this.isPlatformSelected('google');
          default:
            return true;
        }
      });
    }

    return tab.subItems;
  }

  // Method to check if a tab is fully completed
  isTabCompleted(tabId: string): boolean {
    return this.getTabCompletionPercentage(tabId) === 100;
  }

  /**
   * Offer Details is complete only with an offer type, a discount value, a car wash type and at
   * least one location.
   *
   * CWS-1110: this was computed here but `onOfferDetailsDone` marked the section complete
   * unconditionally after a successful save, so a campaign saved with no locations satisfied the
   * publish gate. Both paths read this one predicate now so they cannot drift apart again.
   */
  private isOfferDetailsComplete(): boolean {
    return Boolean(
      this.campaignData.offer_type &&
      this.campaignData.offer_type.trim() !== '' &&
      this.campaignData.offer_discount !== null &&
      this.campaignData.offer_discount !== undefined &&
      this.campaignData.offer_wash_type &&
      this.campaignData.offer_wash_type.length > 0 &&
      this.campaignData.locations &&
      this.campaignData.locations.length > 0
    );
  }

  // Method to update section completion status
  updateSectionCompletion(sectionName: string, isCompleted: boolean): void {
    this.sectionCompletionStatus[sectionName] = isCompleted;
    this.updateTabCompletionStatus();
  }

  // Method to check if a platform is selected
  isPlatformSelected(platform: string): boolean {
    return this.getSelectedPlatformValues().includes(platform);
  }

  /** User segments apply only to email and SMS campaigns. */
  get showUserSegmentSection(): boolean {
    return this.isPlatformSelected('email') || this.isPlatformSelected('sms');
  }

  private getCoreProgressSections(): string[] {
    return [
      'Basic Info',
      'Platform',
      ...(this.showUserSegmentSection ? ['User Segment'] : []),
      'Offer Overview',
      'Offer Details',
      'Usage Limits',
      'Schedule',
    ];
  }

  private syncUserSegmentSectionState(): void {
    if (!this.showUserSegmentSection) {
      this.userSegmentExpanded = false;
      this.updateSectionCompletion('User Segment', true);
      this.campaignData.segment = [];
    }
  }

  private getSelectedPlatformValues(): string[] {
    const platforms = this.campaignData?.platform;
    if (!Array.isArray(platforms)) {
      return [];
    }
    return platforms
      .map((item: any) => (typeof item === 'string' ? item : item?.platform || item?.id || ''))
      .filter((value: string) => value !== '');
  }

  // Method to get filtered tabs based on selected platforms
  /**
   * Returns the component tabs with the 'content' tab's subItems filtered according to selected platforms.
   * @example
   * etFilteredTabs()
   * [{ id: 'content', subItems: ['Email', 'Facebook'] }, { id: 'settings', subItems: [...] }]
   * @param {void} none - This function takes no arguments.
   * @returns {TabItem[]} Array of TabItem objects with platform-filtered subItems for the content tab.
   **/
  getFilteredTabs(): TabItem[] {
    return this.tabs.map(tab => {
      if (tab.id === 'advanced') {
        const progressSections = this.getAdvancedProgressSections();
        return {
          ...tab,
          subItems: tab.subItems,
          completionPercentage: this.getTabCompletionPercentageForSubItems(progressSections),
          isCompleted: this.isTabCompletedForSubItems(progressSections),
        };
      }

      const filteredSubItems = this.getFilteredSubItemsForTab(tab.id);
      return {
        ...tab,
        subItems: filteredSubItems,
        completionPercentage: this.getTabCompletionPercentageForSubItems(filteredSubItems),
        isCompleted: this.isTabCompletedForSubItems(filteredSubItems),
      };
    });
  }

  private getTabCompletionPercentageForSubItems(subItems: string[]): number {
    if (!subItems.length) {
      return 0;
    }
    const completedCount = subItems.filter(subItem => this.isSectionCompleted(subItem)).length;
    return Math.round((completedCount / subItems.length) * 100);
  }

  private isTabCompletedForSubItems(subItems: string[]): boolean {
    return subItems.length > 0 && subItems.every(subItem => this.isSectionCompleted(subItem));
  }

  // Method to manually trigger completion status update
  updateAllCompletionStatus(): void {
    this.autoDetectCompletion();
  }

  // Method to check if all sections are completed
  isAllSectionsCompleted(): boolean {
    const coreCompleted = this.getCoreProgressSections().every(
      s => this.sectionCompletionStatus[s]
    );
    const platformCompleted = PLATFORM_SECTIONS.every(
      ({ platform, section }) =>
        !this.isPlatformSelected(platform) || this.sectionCompletionStatus[section]
    );
    return coreCompleted && platformCompleted;
  }

  isStartDateInPast(): boolean {
    const startDate = this.campaignData?.schedule_start_date;
    if (!startDate || String(startDate).trim() === '') {
      return false;
    }
    const parsed = new Date(startDate);
    if (Number.isNaN(parsed.getTime())) {
      return false;
    }
    return parsed < new Date();
  }

  canPublish(): boolean {
    return !this.isStartDateInPast() && !this.hasPromoCodeConflict;
  }

  get publishButtonTooltip(): string {
    if (this.hasPromoCodeConflict) {
      return 'Promo code already in use for this company';
    }
    if (this.isStartDateInPast()) {
      return 'Start date & time cannot be in the past';
    }
    return '';
  }

  getOverallCompletionPercentage(): number {
    const coreSections = this.getCoreProgressSections();
    const coreCompleted = coreSections.filter(s => this.sectionCompletionStatus[s]).length;
    const activePlatformSections = PLATFORM_SECTIONS.filter(({ platform }) =>
      this.isPlatformSelected(platform)
    );
    const platformCompleted = activePlatformSections.filter(
      ({ section }) => this.sectionCompletionStatus[section]
    ).length;
    const total = coreSections.length + activePlatformSections.length;
    return total > 0 ? Math.round(((coreCompleted + platformCompleted) / total) * 100) : 0;
  }

  // Method to update tab completion status
  updateTabCompletionStatus(): void {
    this.tabs.forEach(tab => {
      tab.completionPercentage = this.getTabCompletionPercentage(tab.id);
      tab.isCompleted = this.isTabCompleted(tab.id);
    });
  }

  /**
   * Initialize the editor tab state by resetting sections, setting the active tab, and opening a section.
   * @example
   * nitializeTabState('details', 'overview')
   * undefined
   * @param {{string}} {{tab}} - Tab identifier to activate.
   * @param {{string}} {{section}} - Optional section name within the tab to open; if omitted, the first section of the tab will be opened.
   * @returns {{void}} Does not return a value.
   **/
  initializeTabState(tab: string, section?: string): void {
    // Reset all sections first
    this.resetAllSections();

    // Set the active tab
    this.activeTab = tab;

    // If a specific section is requested, open it
    if (section) {
      this.openSectionByName(section);
    } else {
      // Open the first section of the tab by default
      this.openFirstSectionOfTab(tab);
    }
  }

  /**
   * Collapse/reset all expandable sections in the campaign editor by setting their expanded flags to false.
   * @example
   * this.esetAllSections()
   * // returns undefined
   * @returns {void} All section "expanded" boolean flags are set to false.
   */
  resetAllSections(): void {
    this.basicInfoExpanded = false;
    this.platformExpanded = false;
    this.utmTaggingExpanded = false;
    this.userSegmentExpanded = false;
    this.offerOverviewExpanded = false;
    this.offerDetailsExpanded = false;
    this.usageLimitsExpanded = false;
    this.emailExpanded = false;
    this.textMessageExpanded = false;
    this.facebookExpanded = false;
    this.googleAdsExpanded = false;
    this.goalsExpanded = false;
    this.scheduleExpanded = false;
  }

  /**
   * Expands the specified editor section by setting its corresponding expanded flag to true.
   * @example
   * penSectionByName('Basic Info')
   * undefined
   * @param {{string}} {{sectionName}} - The name of the section to expand.
   * @returns {{void}} No return value; the function performs side effects by updating component state.
   **/
  openSectionByName(sectionName: string): void {
    const sectionMap: Record<string, () => void> = {
      'Basic Info': () => (this.basicInfoExpanded = true),
      Platform: () => (this.platformExpanded = true),
      'UTM tagging': () => (this.utmTaggingExpanded = true),
      'User Segment': () => {
        if (this.showUserSegmentSection) {
          this.userSegmentExpanded = true;
        }
      },
      'Offer Overview': () => (this.offerOverviewExpanded = true),
      'Offer Details': () => (this.offerDetailsExpanded = true),
      'Usage Limits': () => (this.usageLimitsExpanded = true),
      Email: () => (this.emailExpanded = true),
      'Text Message': () => (this.textMessageExpanded = true),
      Facebook: () => (this.facebookExpanded = true),
      'Google Ads': () => (this.googleAdsExpanded = true),
      'Optional Settings': () => (this.optionalSettingsActiveSection = 'landing'),
      Goals: () => (this.goalsExpanded = true),
      Schedule: () => (this.scheduleExpanded = true),
    };

    if (sectionMap[sectionName]) {
      sectionMap[sectionName]();
    }
  }

  /**
   * Open the first collapsible section associated with the specified campaign editor tab.
   * @example
   * penFirstSectionOfTab('content')
   * undefined
   * @param {{string}} {{tab}} - Tab key ('overview' | 'offers' | 'content' | 'finish') indicating which tab's first relevant section to expand.
   * @returns {{void}} No return value.
   **/
  openFirstSectionOfTab(tab: string): void {
    switch (tab) {
      case 'overview':
        this.basicInfoExpanded = true;
        break;
      case 'offers':
        this.offerOverviewExpanded = true;
        break;
      case 'content':
        // Open the first available platform section based on selected platforms
        if (this.isPlatformSelected('email')) {
          this.emailExpanded = true;
        } else if (this.isPlatformSelected('sms')) {
          this.textMessageExpanded = true;
        } else if (this.isPlatformSelected('facebook')) {
          this.facebookExpanded = true;
        } else if (this.isPlatformSelected('google')) {
          this.googleAdsExpanded = true;
        }
        break;
      case 'advanced':
        this.optionalSettingsActiveSection = 'landing';
        break;
      case 'finish':
        this.goalsExpanded = true;
        break;
    }
  }

  // Helper method to build query params preserving 'new' parameter
  /**
   * Build query parameters object including campaignId if set and preserve the 'new' query parameter when present.
   * @example
   * buildQueryParams()
   * { campaignId: '123', new: 'true' }
   * @returns {any} Query parameters object to use for routing/navigation.
   **/
  private buildQueryParams(): any {
    const params: any = {};
    if (this.campaignId) {
      params['campaignId'] = this.campaignId;
    }
    // Preserve 'new' parameter if it exists
    const currentParams = this.route.snapshot.queryParams;
    if (currentParams['new'] === 'true') {
      params['new'] = 'true';
    }
    return params;
  }

  onTabChange(tabId: string): void {
    // Check if access is allowed
    if (!this.isAccessAllowed(tabId)) {
      this.showAccessRestrictedToast();
      return;
    }

    this.activeTab = tabId;
    const queryParams = this.buildQueryParams();
    this.router.navigate(['/campaign-editor', tabId], { queryParams });
  }

  onSubItemClick(tabId: string, subItem: string): void {
    // Check if access is allowed
    if (!this.isAccessAllowed(tabId)) {
      this.showAccessRestrictedToast();
      return;
    }

    this.activeTab = tabId;
    const queryParams = this.buildQueryParams();
    this.router.navigate(['/campaign-editor', tabId, subItem], { queryParams });
  }

  isAccessAllowed(tabId: string): boolean {
    // If no campaignId, only allow 'overview' tab
    if ((!this.campaignId && !this.campaignData.name) || this.campaignData.name.trim() === '') {
      return tabId === 'overview';
    }
    // If campaignId exists, allow all tabs
    return true;
  }

  showAccessRestrictedToast(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Access Restricted',
      detail: sanitizeErrorMessage('You must fill add basic info to continue'),
      life: 3000,
    });
  }

  /**
   * Update tabs' disabled state in the campaign editor based on presence of campaignId and campaign name.
   * @example
   * pdateTabDisabledState()
   * undefined
   * @returns {{void}} Updates the component's tab.disabled flags in-place; does not return a value.
   */
  updateTabDisabledState(): void {
    // If no campaignId, disable all tabs except 'overview'
    this.tabs.forEach(tab => {
      if (!this.campaignId) {
        tab.disabled = tab.id !== 'overview';
      } else {
        // If campaignId exists, check if campaign name is provided
        const hasCampaignName = this.campaignData.name && this.campaignData.name.trim() !== '';
        tab.disabled = !hasCampaignName;
      }
    });
  }

  // Handle collapsible section toggles
  onBasicInfoToggle(expanded: boolean): void {
    if (expanded) {
      // Close other sections when opening this one
      this.platformExpanded = false;
      this.utmTaggingExpanded = false;
      this.userSegmentExpanded = false;
    }
    this.basicInfoExpanded = expanded;
  }

  /**
   * Toggle the "platform" section open or closed, enforcing access and closing other sections when opened.
   * @example
   * nPlatformToggle(true)
   * undefined
   * @param {{boolean}} {{expanded}} - Whether to expand (true) or collapse (false) the platform section.
   * @returns {{void}} No return value; updates component state and may show an access-restricted toast.
   **/
  onPlatformToggle(expanded: boolean): void {
    // Check if access is allowed (only if campaignId exists)
    if (expanded && !this.campaignId) {
      this.showAccessRestrictedToast();
      return;
    }

    if (expanded) {
      // Close other sections when opening this one
      this.basicInfoExpanded = false;
      this.utmTaggingExpanded = false;
      this.userSegmentExpanded = false;
    }
    this.platformExpanded = expanded;
  }

  onUtmTaggingToggle(expanded: boolean): void {
    if (expanded && !this.campaignId) {
      this.showAccessRestrictedToast();
      return;
    }

    if (expanded) {
      this.basicInfoExpanded = false;
      this.platformExpanded = false;
      this.userSegmentExpanded = false;
    }
    this.utmTaggingExpanded = expanded;
  }

  /**
   * Toggle the User Segment panel's expanded state, enforcing access restrictions and closing other sections when opened.
   * @example
   * nUserSegmentToggle(true)
   * undefined
   * @param {{boolean}} {{expanded}} - Whether to expand the user segment section; if true and no campaignId is set, shows an access-restricted toast and does not expand.
   * @returns {{void}} No return value.
   **/
  onUserSegmentToggle(expanded: boolean): void {
    // Check if access is allowed (only if campaignId exists)
    if (expanded && !this.campaignId) {
      this.showAccessRestrictedToast();
      return;
    }

    if (expanded) {
      // Close other sections when opening this one
      this.basicInfoExpanded = false;
      this.platformExpanded = false;
      this.utmTaggingExpanded = false;
    }
    this.userSegmentExpanded = expanded;
  }

  // Handle done actions
  onBasicInfoDone(): void {
    this.logger.debug('Basic Info section completed');

    if (!this.campaignData.name?.trim() || !this.campaignData.description?.trim()) {
      return;
    }

    const advance = () => {
      this.updateSectionCompletion('Basic Info', true);
      this.basicInfoExpanded = false;
      this.platformExpanded = true;
    };

    if (this.campaignId) {
      this.updateCampaign({ onSuccess: advance, reload: false, resetBasicInfoOnError: true });
      return;
    }

    this.createCampaign({ onSuccess: advance, resetBasicInfoOnError: true });
  }

  onPlatformDone(): void {
    this.logger.debug('Platform section completed');
    this.updateSectionCompletion('Platform', true);
    this.platformExpanded = false;
    this.utmTaggingExpanded = true;
  }

  onUtmPlatformChange(platforms: CampaignPlatformApiItem[]): void {
    this.campaignData.platform = platforms;
    this.updateTabCompletionStatus();
    this.syncUserSegmentSectionState();
    this.autoDetectCompletion();

    if (this.campaignId) {
      this.updateCampaign();
    } else {
      this.createCampaign();
    }
  }

  onUtmTaggingDone(): void {
    this.logger.debug('UTM tagging section completed');
    this.updateSectionCompletion('UTM tagging', true);
    this.utmTaggingExpanded = false;

    if (this.showUserSegmentSection) {
      this.userSegmentExpanded = true;
      return;
    }

    const queryParams = this.buildQueryParams();
    this.router.navigate(['/campaign-editor/offers/Offer Overview'], { queryParams });
  }

  /**
   * Marks the "User Segment" section as completed, updates the campaign immediately, collapses the user segment panel, and navigates to the "Offer Overview" tab while preserving query parameters.
   * @example
   * nUserSegmentDone()
   * // void
   * @returns {{void}} No return value.
   **/
  onUserSegmentDone(): void {
    this.logger.debug('User segment section completed');
    this.updateSectionCompletion('User Segment', true);
    // Update campaign immediately without delay
    this.updateCampaign();
    // Close user segment and open offer overview section
    this.userSegmentExpanded = false;
    // Navigate to Offer Overview tab, preserving campaignId and 'new' parameter if present
    const queryParams = this.buildQueryParams();

    this.router.navigate(['/campaign-editor/offers/Offer Overview'], { queryParams });
  }

  onOfferOverviewToggle(expanded: boolean): void {
    if (expanded) {
      // Close other offer sections when opening this one
      this.offerDetailsExpanded = false;
      this.usageLimitsExpanded = false;
    }
    this.offerOverviewExpanded = expanded;
  }

  onOfferOverviewDone(): void {
    this.logger.debug('Offer overview section completed');
    if (this.isOfferOverviewSaving) {
      return;
    }

    this.isOfferOverviewSaving = true;
    this.isExplicitSaveInProgress = true;
    this.persistCampaign$().subscribe({
      next: response => {
        this.isOfferOverviewSaving = false;
        this.isExplicitSaveInProgress = false;
        if (!response.status) {
          this.handleCampaignPersistError({ error: response });
          return;
        }
        this.applyPersistSuccess(response);
        if (this.hasPromoCodeConflict) {
          return;
        }
        this.updateSectionCompletion('Offer Overview', true);
        this.offerOverviewExpanded = false;
        this.offerDetailsExpanded = true;
      },
      error: error => {
        this.isOfferOverviewSaving = false;
        this.isExplicitSaveInProgress = false;
        this.handleCampaignPersistError(error);
      },
    });
  }

  onOfferDetailsToggle(expanded: boolean): void {
    if (expanded) {
      // Close other offer sections when opening this one
      this.offerOverviewExpanded = false;
      this.usageLimitsExpanded = false;
    }
    this.offerDetailsExpanded = expanded;
  }

  /**
   * Persist the offer details before advancing, the same way Offer Overview does. Relying on the
   * 3-second autosave left the section unsaved whenever the user clicked Done sooner (CWS-1107).
   */
  onOfferDetailsDone(): void {
    this.logger.debug('Offer details section completed');
    if (this.isOfferDetailsSaving) {
      return;
    }

    // Cancel any in-flight autosave first: its late response would refetch and overwrite the
    // offer details this save is about to send. Same reason the publish flow does it.
    this.cancelPendingCampaignUpdate();
    this.isOfferDetailsSaving = true;
    this.isExplicitSaveInProgress = true;
    this.persistCampaign$().subscribe({
      next: response => {
        this.isOfferDetailsSaving = false;
        this.isExplicitSaveInProgress = false;
        if (!response.status) {
          this.handleCampaignPersistError({ error: response });
          return;
        }
        this.applyPersistSuccess(response);
        this.updateSectionCompletion('Offer Details', this.isOfferDetailsComplete());
        this.offerDetailsExpanded = false;
        this.usageLimitsExpanded = true;
      },
      error: error => {
        this.isOfferDetailsSaving = false;
        this.isExplicitSaveInProgress = false;
        this.handleCampaignPersistError(error);
      },
    });
  }

  onUsageLimitsToggle(expanded: boolean): void {
    if (expanded) {
      // Close other offer sections when opening this one
      this.offerOverviewExpanded = false;
      this.offerDetailsExpanded = false;
    }
    this.usageLimitsExpanded = expanded;
  }

  onUsageLimitsDone(): void {
    this.logger.debug('Usage limits section completed');
    this.updateSectionCompletion('Usage Limits', true);

    this.usageLimitsExpanded = false;
    const queryParams = this.buildQueryParams();
    this.router.navigate(['/campaign-editor/content/Email'], { queryParams });
  }

  onEmailToggle(expanded: boolean): void {
    if (expanded) {
      // Close other content sections when opening this one
      this.textMessageExpanded = false;
      this.facebookExpanded = false;
      this.googleAdsExpanded = false;
    }
    this.emailExpanded = expanded;
  }

  /**
   * Mark the Email section as completed, collapse its panel, and advance the UI to the next selected platform section or navigate to the finish tab.
   * @example
   * nEmailDone()
   * undefined
   * @returns {void} Does not return a value; performs UI state updates and navigation side effects.
   */
  onEmailDone(): void {
    this.logger.debug('Email section completed');
    this.updateSectionCompletion('Email', true);
    this.emailExpanded = false;

    // Navigate to next available platform section
    if (this.isPlatformSelected('sms')) {
      this.textMessageExpanded = true;
    } else if (this.isPlatformSelected('facebook')) {
      this.facebookExpanded = true;
    } else if (this.isPlatformSelected('google')) {
      this.googleAdsExpanded = true;
    } else {
      // No more platforms, navigate to finish tab
      const queryParams = this.buildQueryParams();
      this.router.navigate(['/campaign-editor/finish/Goals'], { queryParams });
    }
  }

  onTextMessageToggle(expanded: boolean): void {
    if (expanded) {
      // Close other content sections when opening this one
      this.emailExpanded = false;
      this.facebookExpanded = false;
      this.googleAdsExpanded = false;
    }
    this.textMessageExpanded = expanded;
  }

  /**
   * Mark the Text Message section as completed, collapse the section, update internal completion state, and navigate to the next platform section or the finish tab.
   * @example
   * nTextMessageDone()
   * // undefined (void) â€” updates UI state and triggers navigation as a side effect
   * @returns {void} No return value; performs state updates and routing as side effects.
   **/
  onTextMessageDone(): void {
    this.logger.debug('Text message section completed');
    this.updateSectionCompletion('Text Message', true);
    this.textMessageExpanded = false;

    if (this.isPlatformSelected('facebook')) {
      this.facebookExpanded = true;
    } else if (this.isPlatformSelected('google')) {
      this.googleAdsExpanded = true;
    } else {
      this.navigateToOptionalSettingsLandingPage();
    }
  }

  onFacebookToggle(expanded: boolean): void {
    if (expanded) {
      // Close other content sections when opening this one
      this.emailExpanded = false;
      this.textMessageExpanded = false;
      this.googleAdsExpanded = false;
    }
    this.facebookExpanded = expanded;
  }

  /**
   * Handle completion of the Facebook section: mark it complete, collapse it, and navigate to the next platform section or the finish tab.
   * @example
   * nFacebookDone()
   * // returns void
   * @returns {void} Does not return a value.
   */
  onFacebookDone(): void {
    this.logger.debug('Facebook section completed');
    this.updateSectionCompletion('Facebook', true);
    this.facebookExpanded = false;

    if (this.isPlatformSelected('google')) {
      this.googleAdsExpanded = true;
    } else {
      this.navigateToOptionalSettingsLandingPage();
    }
  }

  onGoogleAdsToggle(expanded: boolean): void {
    if (expanded) {
      // Close other content sections when opening this one
      this.emailExpanded = false;
      this.textMessageExpanded = false;
      this.facebookExpanded = false;
    }
    this.googleAdsExpanded = expanded;
  }

  onGoogleAdsDone(): void {
    this.logger.debug('Google Ads section completed');
    this.updateSectionCompletion('Google Ads', true);
    this.googleAdsExpanded = false;
    this.navigateToOptionalSettingsLandingPage();
  }

  onOptionalSettingsDone(): void {
    this.logger.debug('Optional settings section completed');
    this.getAdvancedProgressSections().forEach(section =>
      this.updateSectionCompletion(section, true)
    );
    this.updateSectionCompletion('Optional Settings', true);
    const queryParams = this.buildQueryParams();
    this.router.navigate(['/campaign-editor/finish/Goals'], { queryParams });
  }

  /** Advanced tab → landing page section (continues optional-settings workflow). */
  private navigateToOptionalSettingsLandingPage(): void {
    this.optionalSettingsActiveSection = 'landing';
    this.activeTab = 'advanced';
    const queryParams = this.buildQueryParams();
    this.router.navigate(['/campaign-editor', 'advanced', 'Optional Settings'], { queryParams });
  }

  onGoalsToggle(expanded: boolean): void {
    if (expanded) {
      // Close other finish sections when opening this one
      this.scheduleExpanded = false;
    }
    this.goalsExpanded = expanded;
  }

  onGoalsDone(): void {
    this.logger.debug('Goals section completed (optional - can be empty)');
    // Goals is optional, so always mark as completed
    this.updateSectionCompletion('Goals', true);
    this.goalsExpanded = false;
    this.scheduleExpanded = true;
  }

  onScheduleToggle(expanded: boolean): void {
    if (expanded) {
      // Close other finish sections when opening this one
      this.goalsExpanded = false;
    }
    this.scheduleExpanded = expanded;
  }

  onScheduleDone(): void {
    this.logger.debug('Schedule section completed');
    this.updateSectionCompletion('Schedule', true);
  }

  onScheduleOpenPreview(): void {
    // Check if all sections are completed before opening preview modal
    if (this.isAllSectionsCompleted()) {
      this.showPreviewModal = true;
    }
  }

  // Preview modal methods
  onPreviewClick(): void {
    this.showPreviewModal = true;
  }

  onClosePreviewModal(): void {
    this.showPreviewModal = false;
  }

  onConfirmAndPublish(): void {
    // Close the modal first
    this.showPreviewModal = false;
    // Call the same publish logic
    this.onPublishClick();
  }

  // Handle window resize for responsive design
  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any): void {
    this.checkScreenSize();
  }

  // Check screen size and update mobile view state
  checkScreenSize(): void {
    this.isMobileView = window.innerWidth < 880;
  }

  // Method to trigger form data collection
  collectFormData(): void {
    this.logger.debug('Collecting form data...', this.campaignData);
    this.logger.debug('Triggering form data subject...');
    this.formChangeSeq += 1;
    this.formDataSubject.next(null);
  }

  // Handle form data changes after 3-second debounce
  /**
   * Handle changes to the campaign form data; if this.campaignId exists it updates the existing campaign, otherwise it creates a new campaign.
   * @example
   * handleFormDataChange()
   * // returns void (performs side effects: create or update campaign)
   * @returns {void} Performs side effects to create or update a campaign based on the current campaignId.
   */
  private handleFormDataChange(): void {
    if (this.isPublishing) {
      return;
    }
    // Skip autosave if an explicit save (e.g., "Done" button) is in progress
    // to avoid race condition where late autosave response reloads stale data
    if (this.isExplicitSaveInProgress) {
      this.logger.debug('Skipping autosave: explicit save in progress');
      return;
    }
    this.logger.debug('Handling form data change. Campaign ID:', this.campaignId);
    if (this.campaignId) {
      // Update existing campaign
      this.logger.debug('Updating existing campaign...');
      this.updateCampaign();
    } else if (!this.createRequestInFlight) {
      // Create new campaign
      this.logger.debug('Creating new campaign...');
      this.createCampaign();
    }
  }

  // Create new campaign
  /**
   * Initiates creation of a campaign by building the payload, calling the campaign service, updating component state on success, and handling errors.
   * @example
   * createCampaign()
   * undefined
   * @returns {{void}} Does not return a value; updates this.campaignId and navigates to merge query params on success, or delegates error handling to the basic-info component on failure.
   **/
  private persistCampaign$(): Observable<any> {
    const seq = this.formChangeSeq;
    const payload = this.buildCampaignPayload();
    const request$ = this.campaignId
      ? this.campaignService.updateCampaign(this.campaignId, payload)
      : this.campaignService.createCampaign(payload);

    return request$.pipe(
      tap(response => {
        if (response?.status) {
          this.commitFormChanges(seq);
        }
      })
    );
  }

  private applyPersistSuccess(response: any): void {
    if (!response?.status) {
      return;
    }

    if (!this.campaignId && response.data?.id) {
      this.campaignId = response.data.id;
      const queryParams = this.buildQueryParams();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });
      this.logger.debug('Campaign created successfully:', response.data);
      return;
    }

    if (this.campaignId) {
      this.logger.debug('Campaign updated successfully:', response.data);
      this.loadCampaignData();
    }
  }

  private createCampaign(
    options: { onSuccess?: () => void; resetBasicInfoOnError?: boolean } = {}
  ): void {
    if (this.campaignId) {
      this.updateCampaign({
        onSuccess: options.onSuccess,
        reload: false,
        resetBasicInfoOnError: options.resetBasicInfoOnError,
      });
      return;
    }

    if (options.onSuccess) {
      this.pendingCreateSuccessCallbacks.push(options.onSuccess);
    }

    if (this.createRequestInFlight) {
      return;
    }

    const seq = this.formChangeSeq;
    const payload = this.buildCampaignPayload();
    const attemptedName = payload.name;

    this.createRequestInFlight = true;
    this.campaignService
      .createCampaign(payload)
      .pipe(
        finalize(() => {
          this.createRequestInFlight = false;
        })
      )
      .subscribe({
        next: response => {
          if (response.status && response.data) {
            this.commitFormChanges(seq);
            this.campaignId = response.data.id;
            // Update URL with campaign ID, but keep 'new=true' parameter if it exists
            // This preserves the isNewCampaign state
            const queryParams = this.buildQueryParams();
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: queryParams,
              queryParamsHandling: 'merge',
            });
            this.logger.debug('Campaign created successfully:', response.data);
            const callbacks = this.pendingCreateSuccessCallbacks.splice(0);
            callbacks.forEach(callback => callback());
          }
        },
        error: error => {
          this.pendingCreateSuccessCallbacks = [];
          this.handleCampaignSaveError(
            error,
            options.resetBasicInfoOnError === true,
            attemptedName
          );
        },
      });
  }

  // Update existing campaign
  /**
   * Update the current campaign by building a payload and invoking the campaignService to persist changes; handles success and error responses (including forwarding API validation errors to the basic-info component) and exits early if no campaignId is set.
   * @example
   * updateCampaign()
   * // void
   * @returns {void} Performs an asynchronous update operation; no value is returned.
   */
  private updateCampaign(
    options: { onSuccess?: () => void; reload?: boolean; resetBasicInfoOnError?: boolean } = {}
  ): void {
    if (!this.campaignId || this.isPublishing) return;

    this.cancelPendingCampaignUpdate();
    const seq = this.formChangeSeq;
    const payload = this.buildCampaignPayload();
    const generation = this.campaignUpdateGeneration;
    const shouldReload = options.reload !== false;
    const attemptedName = payload.name;

    this.campaignUpdateSub = this.campaignService
      .updateCampaign(this.campaignId, payload)
      .subscribe({
        next: response => {
          if (generation !== this.campaignUpdateGeneration || this.isPublishing) {
            return;
          }
          if (response.status) {
            this.commitFormChanges(seq);
            this.logger.debug('Campaign updated successfully:', response.data);
            if (shouldReload) {
              this.loadCampaignData();
            }
            options.onSuccess?.();
          }
        },
        error: error => {
          if (generation !== this.campaignUpdateGeneration || this.isPublishing) {
            return;
          }
          this.handleCampaignSaveError(
            error,
            options.resetBasicInfoOnError === true,
            attemptedName
          );
        },
      });
  }

  /**
   * Show a save-failure toast, persist promo conflict on the parent, and
   * forward name conflicts to Basic Info when that child is mounted.
   */
  private handleCampaignPersistError(error: any): void {
    this.logger.error('Error saving campaign:', error);
    const apiMessage = extractErrorMessage(error, 'Failed to save campaign.');
    this.messageService.add({
      severity: 'error',
      summary: 'Save Failed',
      detail: sanitizeErrorMessage(apiMessage),
      life: 3000,
    });

    const apiError = error?.error ?? error;
    if (apiError?.message && this.isNameConflictMessage(apiError.message)) {
      this.basicInfoComponent?.handleApiError(apiError);
    }

    // Only update promo conflict state if this is a promo code error; don't clear existing conflicts for unrelated errors
    if (this.isPromoCodeInUseMessage(apiMessage)) {
      this.setPromoCodeConflict(apiMessage);
    }
  }

  private isNameConflictMessage(message: string): boolean {
    return message.toLowerCase().includes('name');
  }

  private isPromoCodeInUseMessage(message: string): boolean {
    return message.includes('Promo code already in use');
  }

  private setPromoCodeConflict(message: string): void {
    this.hasPromoCodeConflict = this.isPromoCodeInUseMessage(message);
    this.promoCodeConflictMessage = this.hasPromoCodeConflict ? message : '';
    if (this.hasPromoCodeConflict) {
      this.updateSectionCompletion('Offer Overview', false);
    }
  }

  private clearPromoCodeConflict(): void {
    this.hasPromoCodeConflict = false;
    this.promoCodeConflictMessage = '';
  }

  private handleCampaignSaveError(
    error: unknown,
    resetBasicInfoOnError: boolean,
    attemptedName?: string
  ): void {
    this.handleCampaignPersistError(error);

    if (resetBasicInfoOnError) {
      this.updateSectionCompletion('Basic Info', false);
      this.basicInfoExpanded = true;
      this.platformExpanded = false;
    }

    const apiError = (error as { error?: { message?: string } })?.error;
    if (this.basicInfoComponent && apiError?.message) {
      if (resetBasicInfoOnError) {
        this.basicInfoComponent.handleApiError(apiError, attemptedName);
      } else {
        this.basicInfoComponent.handleApiError(apiError);
      }
    }
  }

  // Build campaign payload from form data
  private buildCampaignPayload(): any {
    // Always use JSON payload now that files are uploaded separately
    return this.buildCampaignJsonPayload();
  }

  // Build JSON payload for campaign API
  /**
   * Build a JSON payload object for the current campaign using this.campaignData and contents from campaignDataManager.
   * @example
   * buildCampaignJsonPayload()
   * { campaign_status: 'draft', name: 'Summer Promo', offer_name: '10OFF', contents: [{...}], ... }
   * @param {{void}} {{none}} - This method does not accept any arguments.
   * @returns {{any}} Return the assembled campaign payload object ready to be submitted or serialized.
   **/
  private buildCampaignJsonPayload(): any {
    const payload: any = {};

    console.log('Building campaign JSON payload with campaign data:', this.campaignData);
    // Add basic campaign fields
    payload.campaign_status = this.campaignData.campaign_status || 'draft';
    payload.name = this.campaignData.name?.trim();
    payload.description = this.campaignData.description?.trim();
    payload.platforms = (this.campaignData.platform || [])
      .map((item: any) => {
        if (typeof item === 'string') {
          return { platform: item };
        }
        if (item && typeof item === 'object') {
          const platformValue = String(item.platform || item.id || '').trim();
          const entry: Record<string, any> = { platform: platformValue };
          const utmFields = [
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_content',
            'utm_term',
          ] as const;
          utmFields.forEach(key => {
            if (item[key] !== undefined && item[key] !== null && String(item[key]).trim() !== '') {
              entry[key] = item[key];
            }
          });
          return entry;
        }
        return { platform: String(item || '') };
      })
      .filter((item: { platform: string }) => item.platform.trim() !== '');
    payload.segment = this.showUserSegmentSection ? this.campaignData.segment : [];
    payload.locations = this.campaignData.locations;
    this.logger.debug('packages', this.campaignData.packages);
    this.logger.debug(' packages payload.offer_wash_type', this.campaignData.offer_wash_type);
    if (typeof this.campaignData.offer_wash_type === 'string') {
      payload.offer_wash_type = this.campaignData.offer_wash_type;
    } else {
      payload.offer_wash_type = this.campaignData.offer_wash_type;
    }
    // Add offer details
    payload.offer_name = this.campaignData.offer_name;
    payload.promo_code = this.campaignData.promo_code;
    payload.offer_description = this.campaignData.offer_description;
    payload.offer_type = this.campaignData.offer_type || 'flat_discount';
    payload.max_discount = this.campaignData.max_discount;
    payload.offer_discount = this.campaignData.offer_discount;
    payload.quantity_limit = this.campaignData.quantity_limit;
    payload.multi_use_limit = this.campaignData.multi_use_limit;
    payload.usage_type = this.campaignData.usage_type || 'single';

    // Add schedule details
    payload.schedule_start_date = this.campaignData.schedule_start_date;
    payload.schedule_start_time = this.campaignData.schedule_start_time;
    payload.schedule_end_date = this.campaignData.schedule_end_date;
    payload.schedule_end_time = this.campaignData.schedule_end_time;
    payload.schedule_recurrence = this.campaignData.schedule_recurrence || 'daily';

    // Add goals
    payload.goals = this.campaignData.goals;

    // Add contents from centralized data manager
    const allContents = this.campaignDataManager.getAllContents();
    if (allContents && allContents.length > 0) {
      payload.contents = allContents.map((content: any) => {
        const processedContent = { ...content };
        // Remove File objects and keep only URLs
        Object.keys(processedContent).forEach(key => {
          const value = processedContent[key];
          if (value instanceof File) {
            // Skip File objects - they should have been uploaded and replaced with URLs
            delete processedContent[key];
          }
        });
        return processedContent;
      });
    }

    this.logger.debug('JSON payload:', payload);
    return payload;
  }

  // Method to update campaign data from child components
  /**
   * Merge provided campaign data into the component's campaignData and trigger related UI/state updates.
   * @example
   * pdateCampaignData({ platform: 'ios', name: 'Spring Campaign' })
   * undefined
   * @param {{any}} {{data}} - Partial campaign data to merge into this.campaignData and trigger updates.
   * @returns {{void}} No return value; performs side effects (logging, form collection, tab state/completion updates).
   **/
  updateCampaignData(data: any): void {
    this.logger.debug('Updating campaign data with:', data);
    if (data?.promo_code !== undefined && data.promo_code !== this.campaignData.promo_code) {
      this.clearPromoCodeConflict();
    }
    const platformChanged = data.platform !== undefined;

    if (platformChanged && Array.isArray(data.platform)) {
      data = {
        ...data,
        platform: this.mergePlatformSelection(data.platform),
      };
    }

    Object.assign(this.campaignData, data);
    this.logger.debug('Updated campaign data:', this.campaignData);
    this.updateTabDisabledState();
    this.autoDetectCompletion();

    if (platformChanged) {
      this.updateTabCompletionStatus();
      this.syncUserSegmentSectionState();
      if (this.campaignId) {
        this.updateCampaign();
      } else {
        this.createCampaign();
      }
      return;
    }

    this.collectFormData();
  }

  /** Keeps existing UTM fields when platform checkboxes change. */
  private mergePlatformSelection(selectedIds: string[]): any[] {
    const existingById = new Map<string, any>();

    for (const item of this.campaignData.platform || []) {
      const id =
        typeof item === 'string' ? item.trim() : String(item?.platform || item?.id || '').trim();
      if (id) {
        existingById.set(id, item);
      }
    }

    return selectedIds
      .map(id => {
        const platformId = String(id || '').trim();
        if (!platformId) {
          return null;
        }
        const existing = existingById.get(platformId);
        if (existing && typeof existing === 'object') {
          return { ...existing, platform: platformId };
        }
        return { platform: platformId };
      })
      .filter(Boolean);
  }

  // Method to handle content data changes from child components
  /**
   * Handle content data changes for a specific platform, update campaign data and persist changes.
   * @example
   * nContentDataChange('email', { subject: 'Welcome', body: 'Hello!' })
   * undefined
   * @param {{'email'|'facebook'|'sms'|'google'}} {{platform}} - Platform identifier to update.
   * @param {{any}} {{data}} - Content data payload to set for the given platform.
   * @returns {{void}} No return value; updates internal campaign state and triggers create/update and UI state checks.
   **/
  onDeliveryChange(payload: {
    platform: 'sms_delivery' | 'mms' | 'rcs' | 'email_delivery';
    sms_config?: any;
    email_config?: any;
  }): void {
    this.onDeliveryBatchChange([payload as DeliveryContentApi]);
  }

  /** Applies SMS + Email (and MMS/RCS) delivery updates in one pass, then a single API save. */
  onDeliveryBatchChange(payloads: DeliveryContentApi[]): void {
    if (!payloads?.length) {
      return;
    }
    for (const payload of payloads) {
      this.applyDeliveryPayloadToManager(payload);
    }
    const allContents = this.campaignDataManager.getAllContents();
    this.campaignData.contents = allContents;
    this.logger.debug('Updated campaign data with delivery batch:', this.campaignData);
    if (this.campaignId) {
      this.updateCampaign();
    } else {
      this.createCampaign();
    }
    this.updateTabDisabledState();
    this.autoDetectCompletion();
  }

  private applyDeliveryPayloadToManager(payload: DeliveryContentApi): void {
    if (
      payload.platform === 'email_delivery' &&
      'email_config' in payload &&
      payload.email_config
    ) {
      this.campaignDataManager.updatePlatformData('delivery_email', payload);
      return;
    }
    if (payload.platform === 'mms') {
      this.campaignDataManager.updatePlatformData('mms', payload);
      return;
    }
    if (payload.platform === 'rcs') {
      this.campaignDataManager.updatePlatformData('rcs', payload);
      return;
    }
    if (payload.platform === 'sms_delivery') {
      this.campaignDataManager.updatePlatformData('sms_delivery', payload);
    }
  }

  onDeliveryEmailClear(): void {
    this.onContentDataClear('delivery_email');
  }

  onDeliveryClear(): void {
    this.onContentDataClear('sms_delivery');
    this.onContentDataClear('mms');
    this.onContentDataClear('rcs');
    this.onContentDataClear('delivery_email');
  }

  onContentDataChange(platform: CampaignContentPlatform, data: any): void {
    this.logger.debug(`Updating ${platform} content data:`, data);
    this.campaignDataManager.updatePlatformData(platform, data);

    // Update campaign data with all contents
    const allContents = this.campaignDataManager.getAllContents();
    this.campaignData.contents = allContents;

    this.logger.debug('Updated campaign data with all contents:', this.campaignData);

    // Call update API immediately without debounce or setTimeout
    if (this.campaignId) {
      this.updateCampaign();
    } else {
      this.createCampaign();
    }

    this.updateTabDisabledState();
    this.autoDetectCompletion();
  }

  // Method to clear content data from child components
  /**
   * Clear content data for the specified platform and update campaign state and UI.
   * @example
   * nContentDataClear('email')
   * undefined
   * @param {'email'|'facebook'|'sms'|'google'} platform - Platform identifier whose content data will be cleared.
   * @returns {void} No return value; performs state updates and UI side effects.
   **/
  onContentDataClear(
    platform: CampaignContentPlatform,
    section?: 'landing_page' | 'success_page'
  ): void {
    this.logger.debug(`Clearing ${platform} content data`, section);
    this.campaignDataManager.clearPlatformData(platform, section);

    // Update campaign data with all contents
    const allContents = this.campaignDataManager.getAllContents();
    this.campaignData.contents = allContents;

    this.logger.debug('Updated campaign data after clearing:', this.campaignData);
    this.collectFormData();
    this.updateTabDisabledState();
    this.autoDetectCompletion();
  }

  // Auto-detect completion status based on form data
  /**
   * Evaluate each campaign editor section's completion based on campaignData and isNewCampaign, then update section completion state.
   * @example
   * utoDetectCompletion()
   * void
   * @returns {void} Updates internal section completion state via updateSectionCompletion; no return value.
   **/
  autoDetectCompletion(): void {
    // Basic Info completion - requires campaign name and description (both minimum 2 characters)
    const basicInfoComplete =
      this.campaignData.name &&
      this.campaignData.name.trim() !== '' &&
      this.campaignData.name.trim().length >= 2 &&
      this.campaignData.description &&
      this.campaignData.description.trim() !== '' &&
      this.campaignData.description.trim().length >= 2;
    this.updateSectionCompletion('Basic Info', basicInfoComplete);

    // Platform completion - requires at least one platform selected
    const platformComplete = this.campaignData.platform && this.campaignData.platform.length > 0;
    this.updateSectionCompletion('Platform', platformComplete);

    const utmTaggingComplete = platformComplete;
    this.updateSectionCompletion('UTM tagging', utmTaggingComplete);

    // User Segment completion - required only for email/SMS campaigns
    if (this.showUserSegmentSection) {
      const userSegmentComplete = this.campaignData.segment && this.campaignData.segment.length > 0;
      this.updateSectionCompletion('User Segment', userSegmentComplete);
    } else {
      this.syncUserSegmentSectionState();
    }

    // Offer Overview completion - requires all three fields: offer name, promo code, and offer description
    const offerOverviewComplete =
      !this.hasPromoCodeConflict &&
      this.campaignData.offer_name &&
      this.campaignData.offer_name.trim() !== '' &&
      this.campaignData.promo_code &&
      this.campaignData.promo_code.trim() !== '' &&
      this.campaignData.offer_description &&
      this.campaignData.offer_description.trim() !== '';
    this.updateSectionCompletion('Offer Overview', offerOverviewComplete);

    // Offer Details completion - requires offer type, discount value, car wash types, and locations
    this.updateSectionCompletion('Offer Details', this.isOfferDetailsComplete());

    // Usage Limits completion - mark as completed if:
    // 1. Not in creation mode (edit mode), OR
    // 2. Data exists (usage_type is set or any usage limit fields have values)
    // This ensures that after clicking "Done" and API refetches data, it remains completed
    // In creation mode, campaignId exists due to auto-save, so we check for actual data presence
    const hasUsageLimitsData =
      (this.campaignData.usage_type && this.campaignData.usage_type.trim() !== '') ||
      (this.campaignData.max_discount !== null && this.campaignData.max_discount !== undefined) ||
      (this.campaignData.quantity_limit !== null &&
        this.campaignData.quantity_limit !== undefined) ||
      (this.campaignData.usage_type === 'multiple' &&
        this.campaignData.multi_use_limit !== null &&
        this.campaignData.multi_use_limit !== undefined);
    const usageLimitsComplete = !this.isNewCampaign || hasUsageLimitsData;
    this.updateSectionCompletion('Usage Limits', usageLimitsComplete);

    // Email completion - requires email content with subject and meaningful content (only if email platform is selected)
    const emailContent = findEmailContent(this.campaignData.contents);
    const emailComplete =
      this.isPlatformSelected('email') &&
      Boolean(emailContent?.email_subject?.trim()) &&
      Boolean(
        emailContent?.email_header?.trim() ||
        emailContent?.email_body_text?.trim() ||
        emailContent?.email_button_text?.trim() ||
        emailContent?.email_footer_name?.trim()
      );
    this.updateSectionCompletion('Email', emailComplete);

    // Text Message completion — either SMS body or mms_campaign image/body counts
    const smsContent = findSmsContent(this.campaignData.contents);
    const mmsCampaignContent = Array.isArray(this.campaignData.contents)
      ? this.campaignData.contents.find((c: any) => c.platform === 'mms_campaign')
      : null;
    const textMessageComplete =
      this.isPlatformSelected('sms') &&
      (Boolean(smsContent?.sms_body?.trim()) ||
        Boolean(
          mmsCampaignContent?.mms_config?.bubble_1_coupon?.coupon_image_url?.trim() ||
          mmsCampaignContent?.mms_config?.mms_fallback?.text?.trim()
        ));
    this.updateSectionCompletion('Text Message', textMessageComplete);

    // Facebook completion - requires Facebook content with post text (only if Facebook platform is selected)
    const facebookContent = findFacebookContent(this.campaignData.contents);
    const facebookComplete =
      this.isPlatformSelected('facebook') && Boolean(facebookContent?.fb_caption?.trim());
    this.updateSectionCompletion('Facebook', facebookComplete);

    // Google Ads completion - requires Google Ads content with title (only if Google Ads platform is selected)
    const googleContent = findGoogleAdsContent(this.campaignData.contents);
    const googleAdsComplete =
      this.isPlatformSelected('google') && Boolean(googleContent?.g_ad_title?.trim());
    this.updateSectionCompletion('Google Ads', googleAdsComplete);

    const contents = this.campaignData.contents;
    const landingPageComplete = isOptionalSettingsLandingComplete(contents);
    const successPageComplete = isOptionalSettingsSuccessComplete(contents);
    const needsDelivery = this.isPlatformSelected('sms') || this.isPlatformSelected('email');
    const deliveryComplete = !needsDelivery || isOptionalSettingsDeliveryComplete(contents);

    this.updateSectionCompletion('Landing Page', landingPageComplete);
    this.updateSectionCompletion('Success Page', successPageComplete);
    this.updateSectionCompletion('SMS/Email Delivery', deliveryComplete);
    this.updateSectionCompletion(
      'Optional Settings',
      isOptionalSettingsCompleteFromContents(contents, {
        smsPlatformSelected: this.isPlatformSelected('sms'),
        emailPlatformSelected: this.isPlatformSelected('email'),
      })
    );

    // Goals completion - mark as completed if:
    // 1. Not in creation mode (edit mode), OR
    // 2. Goals data exists (goals array has items with data)
    // This ensures that after clicking "Done" and API refetches data, it remains completed
    // In creation mode, campaignId exists due to auto-save, so we check for actual data presence
    // Goals section is optional, but once filled or "Done" is clicked, it should stay completed
    const hasGoalsData =
      this.campaignData.goals &&
      Array.isArray(this.campaignData.goals) &&
      this.campaignData.goals.length > 0;
    const goalsComplete = !this.isNewCampaign || hasGoalsData;
    this.updateSectionCompletion('Goals', goalsComplete);

    // Schedule completion - requires start date/time; recurring schedules also require end date
    const recurrence = (this.campaignData.schedule_recurrence || 'no_recurrence').toLowerCase();
    const recurringNeedsEnd = ['daily', 'weekly', 'monthly'].includes(recurrence);
    const hasEndDate =
      !!this.campaignData.schedule_end_date && this.campaignData.schedule_end_date.trim() !== '';
    const scheduleComplete =
      !!this.campaignData.schedule_start_date &&
      this.campaignData.schedule_start_date.trim() !== '' &&
      !!this.campaignData.schedule_start_time &&
      this.campaignData.schedule_start_time.trim() !== '' &&
      (!recurringNeedsEnd || hasEndDate);
    this.updateSectionCompletion('Schedule', scheduleComplete);
  }

  // Reset campaign data for new campaign
  /**
   * Reset campaign-related data and UI state to their initial values.
   * @example
   * this.resetCampaignData()
   * void
   * @returns {{void}} Does not return a value; sets isNewCampaign to true, restores campaignData to defaults, resets campaignDataManager, clears sectionCompletionStatus, and updates tab disabled and completion states.
   */
  private resetCampaignData(): void {
    // Set flag to indicate this is a new campaign
    this.isNewCampaign = true;

    // Reset campaign data to initial state
    this.campaignData = getDefaultCampaignData();

    // Reset centralized data manager
    this.campaignDataManager.resetAllData();

    // Reset completion status
    Object.keys(this.sectionCompletionStatus).forEach(key => {
      this.sectionCompletionStatus[key] = false;
    });

    // Update tab disabled state
    this.updateTabDisabledState();

    // Update tab completion status
    this.updateTabCompletionStatus();
  }

  // Load existing campaign data for editing
  /**
   * Load campaign data from the backend and initialize the editor state, tabs, and section completion status.
   * @example
   * loadCampaignData()
   * // returns nothing; initializes campaignData, updates completion states, and triggers change detection
   * @returns {{void}} Does not return a value; updates internal component state and UI.
   */
  private loadCampaignData(): void {
    if (!this.campaignId) return;

    // Don't change isNewCampaign here - it's determined by the 'new' query parameter
    // Only set it if 'new' parameter is explicitly false or not present
    const isNewParam = this.route.snapshot.queryParams['new'];
    if (isNewParam !== 'true') {
      this.isNewCampaign = false;
    }

    // Preserve completion status for Usage Limits and Goals before refetching
    // This ensures that if "Done" was clicked, completion status persists after refetch
    const wasUsageLimitsCompleted = this.sectionCompletionStatus['Usage Limits'];
    const wasGoalsCompleted = this.sectionCompletionStatus['Goals'];

    this.campaignService.getCampaign(this.campaignId).subscribe({
      next: response => {
        console.log('all Campaign data loaded:', response);
        // The user edited a section while this refetch was in flight. The response is older than
        // those edits, so overwriting campaignData here would drop them before they reach the API
        // (CWS-1107). The save they are waiting on triggers its own refetch afterwards.
        if (this.hasUnsavedFormChanges) {
          this.logger.debug('Skipping campaign refetch: unsaved section edits pending');
          return;
        }
        if (response.status && response.data) {
          const campaign = response.data;

          // Populate campaign data with existing values
          this.campaignData = {
            name: campaign.name || '',
            description: campaign.description || '',
            campaign_status: campaign.campaign_status || 'draft',
            platform: campaign.platforms || [],
            segment: campaign.segments?.map(s => s.id) || [],
            locations: campaign.locations?.map(l => l.id) || [],
            packages: campaign.packages?.map(p => p.id) || [],
            contents: campaign.contents || [],
            goals: campaign.goals || [],
            offer_name: campaign.offer_name || '',
            promo_code: campaign.promo_code || '',
            offer_description: campaign.offer_description || '',
            offer_type: campaign.offer_type || '',
            max_discount: campaign.max_discount || null,
            offer_discount: campaign.offer_discount || null,
            offer_wash_type: campaign.offer_wash_type || '',
            quantity_limit: campaign.quantity_limit || null,
            usage_type: campaign.usage_type || '',
            schedule_start_date: campaign.schedule_start_date || '',
            schedule_start_time: campaign.schedule_start_time || '',
            schedule_end_date: campaign.schedule_end_date || '',
            schedule_end_time: campaign.schedule_end_time || '',
            schedule_recurrence: campaign.schedule_recurrence || '',
          };

          this.logger.debug('Campaign data loaded:', this.campaignData);

          // Initialize the centralized data manager with existing campaign data
          this.campaignDataManager.initializeFromCampaignData(this.campaignData);
          const normalizedContents = this.campaignDataManager.getAllContents();
          if (normalizedContents.length > 0) {
            this.campaignData.contents = normalizedContents;
          }

          // Update tab disabled state after loading campaign data
          this.updateTabDisabledState();

          // Update tab completion status based on loaded data
          this.updateTabCompletionStatus();

          // Update completion status based on loaded data
          this.autoDetectCompletion();

          // In creation mode, preserve completion status if section was previously completed
          // This handles the case where "Done" was clicked but data might be empty (Goals is optional)
          if (this.isNewCampaign) {
            if (wasUsageLimitsCompleted) {
              this.updateSectionCompletion('Usage Limits', true);
            }
            if (wasGoalsCompleted) {
              this.updateSectionCompletion('Goals', true);
            }
          }

          // In edit mode, always mark Usage Limits and Goals as completed
          // (they are optional sections but should show as completed when editing existing campaigns)
          if (!this.isNewCampaign) {
            this.updateSectionCompletion('Usage Limits', true);
            this.updateSectionCompletion('Goals', true);
          }

          // Trigger change detection to update child components
          // This ensures email component gets the updated campaign data
          setTimeout(() => {
            // Force change detection for child components
            this.campaignData = { ...this.campaignData };
          }, 0);
        }
      },
      error: error => {
        this.logger.error('Error loading campaign data:', error);
      },
    });
  }

  // Load segments for preview modal
  /**
   * Loads user segments from the API and maps them into the component's segmentsList property.
   * @example
   * loadSegments()
   * // no return value; segmentsList is populated asynchronously via subscription
   * @returns {void} Populates this.segmentsList with mapped segment objects; performs no synchronous return.
   **/
  private loadSegments(): void {
    this.apiService.getSegments().subscribe({
      next: response => {
        if (response.status && response.data && response.data.userSegment) {
          this.segmentsList = response.data.userSegment.map((segment: Segment) => ({
            id: segment.id,
            name: segment.name,
            description: segment.description,
          }));
        }
      },
      error: error => {
        this.logger.error('Error loading segments:', error);
      },
    });
  }

  // Load locations for preview modal
  /**
   * Loads active locations from the LocationService and assigns them to this.locationsList; filters by truthy status and logs errors to the console.
   * @example
   * loadLocations()
   * undefined
   * @returns {{void}} No return value; performs side-effects by updating component state.
   **/
  private loadLocations(): void {
    this.locationService.getLocations().subscribe({
      next: response => {
        if (response.status && response.data && Array.isArray(response.data)) {
          this.locationsList = response.data.filter(
            (location: LocationApiResponse) => location.status
          );
        }
      },
      error: error => {
        this.logger.error('Error loading locations:', error);
      },
    });
  }

  // Navigate back to campaign list
  navigateToCampaign(): void {
    this.router.navigate(['/campaign']);
  }

  // Publish button click handler
  /**
   * Trigger the publish flow for the current campaign: validates completion and campaign ID, sets a publishing state, calls the publish API, displays success or error messages, and navigates on success.
   * @example
   * nPublishClick()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Performs side effects (API call, messaging, navigation) and returns nothing.
   **/
  onPublishClick(): void {
    // Check if all sections are completed before allowing publish
    if (!this.isAllSectionsCompleted()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Publish',
        detail: 'Please complete all sections before publishing the campaign.',
        life: 3000,
      });
      return;
    }

    if (this.hasPromoCodeConflict) {
      this.messageService.add({
        severity: 'error',
        summary: 'Cannot Publish',
        detail: 'Promo code already in use for this company',
        life: 3000,
      });
      return;
    }

    if (this.isStartDateInPast()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Publish',
        detail: 'Start date & time cannot be in the past.',
        life: 3000,
      });
      return;
    }

    // Check if campaignId exists
    if (!this.campaignId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: sanitizeErrorMessage('Campaign ID not found. Please save the campaign first.'),
        life: 3000,
      });
      return;
    }

    if (this.isPublishing) {
      return;
    }

    // Cancel any in-flight autosave so it cannot overwrite the reviewed payload
    // between this update and publish. Block autosave until the pre-publish save finishes.
    this.cancelPendingCampaignUpdate();
    this.isPublishing = true;
    this.isExplicitSaveInProgress = true;
    const campaignId = this.campaignId;
    const seq = this.formChangeSeq;
    const payload = this.buildCampaignPayload();
    const generation = this.campaignUpdateGeneration;

    this.campaignUpdateSub = this.campaignService.updateCampaign(campaignId, payload).subscribe({
      next: updateResponse => {
        if (generation !== this.campaignUpdateGeneration) {
          return;
        }
        this.isExplicitSaveInProgress = false;
        if (!updateResponse.status) {
          this.isPublishing = false;
          this.handleCampaignPersistError({ error: updateResponse });
          return;
        }
        this.commitFormChanges(seq);
        this.publishCampaignAfterSave(campaignId);
      },
      error: error => {
        if (generation !== this.campaignUpdateGeneration) {
          return;
        }
        this.isPublishing = false;
        this.isExplicitSaveInProgress = false;
        this.handleCampaignPersistError(error);
      },
    });
  }

  /** Publish after a successful pre-publish save; shows success modal or error toast. */
  private publishCampaignAfterSave(campaignId: string): void {
    if (!campaignId) {
      this.isPublishing = false;
      return;
    }

    this.campaignService.publishCampaign(campaignId).subscribe({
      next: response => {
        this.isPublishing = false;
        if (response.status) {
          this.openCampaignSuccessModal();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Publish Failed',
            detail: sanitizeErrorMessage(
              response.message || 'Failed to publish campaign. Please try again.'
            ),
            life: 3000,
          });
        }
      },
      error: error => {
        this.isPublishing = false;
        this.logger.error('Error publishing campaign:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Publish Failed',
          detail: sanitizeErrorMessage(
            error.error?.message || 'Failed to publish campaign. Please try again.'
          ),
          life: 3000,
        });
      },
    });
  }

  // Handle navigation from preview modal
  /**
   * Navigate the editor to a specific tab and optional section, close any open preview modal, and preserve existing query parameters.
   * @example
   * nNavigateToTab({tabId: 'settings', section: 'privacy'})
   * void
   * @param {{tabId: string, section?: string}} navigation - Navigation target containing the required tabId and an optional section.
   * @returns {{void}} No return value.
   **/
  onNavigateToTab(navigation: { tabId: string; section?: string }): void {
    // Close the preview modal
    this.showPreviewModal = false;

    // Set the active tab
    this.activeTab = navigation.tabId;

    // Build query params preserving campaignId and 'new' parameter
    const queryParams = this.buildQueryParams();

    // Navigate to the specified tab and section (matching onSubItemClick pattern)
    this.router.navigate(['/campaign-editor', navigation.tabId, navigation.section || ''], {
      queryParams,
    });
  }

  /** Reload campaign platforms from API and show post-publish success modal. */
  private openCampaignSuccessModal(): void {
    if (!this.campaignId) {
      return;
    }

    this.campaignService.getCampaign(this.campaignId).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.campaignData = {
            ...this.campaignData,
            platform: response.data.platforms || [],
          };
        }
        this.showSuccessModal = true;
      },
      error: error => {
        this.logger.error('Error loading campaign after publish:', error);
        this.showSuccessModal = true;
      },
    });
  }

  onCloseCampaignSuccessModal(): void {
    this.showSuccessModal = false;
    this.router.navigate(['/campaign']);
  }
}
